import { supabase } from '../lib/supabase';

export interface ApiKey {
  id: string;
  user_id: string;
  company_id?: string;
  service: 'groq' | 'mistral' | 'openai' | 'anthropic' | 'google' | 'azure' | 'other';
  api_key: string;
  is_active: boolean;
  is_used_by: string[];
  created_at: string;
  updated_at: string;
  last_used_at?: string;
  usage_count: number;
}

export interface CreateApiKeyData {
  service: 'groq' | 'mistral' | 'openai' | 'anthropic' | 'google' | 'azure' | 'other';
  api_key: string;
  company_id?: string;
}

export interface UpdateApiKeyData {
  api_key?: string;
  is_active?: boolean;
  is_used_by?: string[];
}

export class ApiKeysService {
  // Get all API keys for the current user
  static async getUserApiKeys(): Promise<ApiKey[]> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user API keys:', error);
      throw error;
    }
  }

  // Get API keys for a specific company
  static async getCompanyApiKeys(companyId: string): Promise<ApiKey[]> {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching company API keys:', error);
      throw error;
    }
  }

  // Get all API keys (personal + company) for the current user
  static async getAllUserApiKeys(): Promise<ApiKey[]> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get personal API keys
      const { data: personalKeys, error: personalError } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', user.id)
        .is('company_id', null);

      if (personalError) throw personalError;

      // Get company API keys where user is a member
      const { data: companyKeys, error: companyError } = await supabase
        .from('api_keys')
        .select('*')
        .not('company_id', 'is', null)
        .contains('is_used_by', [user.id]);

      if (companyError) throw companyError;

      // Combine and sort by creation date
      const allKeys = [...(personalKeys || []), ...(companyKeys || [])];
      return allKeys.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } catch (error) {
      console.error('Error fetching all user API keys:', error);
      throw error;
    }
  }

  // Create a new API key
  static async createApiKey(apiKeyData: CreateApiKeyData): Promise<ApiKey> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // If company_id is provided, verify user has access to that company
      if (apiKeyData.company_id) {
        const { data: company, error: companyError } = await supabase
          .from('companies')
          .select('member_ids')
          .eq('id', apiKeyData.company_id)
          .single();

        if (companyError || !company) {
          throw new Error('Company not found');
        }

        if (!company.member_ids?.includes(user.id)) {
          throw new Error('You do not have access to this company');
        }
      }

      // Check if the exact API key value already exists (to prevent duplicates)
      const { data: existingKey, error: checkError } = await supabase
        .from('api_keys')
        .select('id, api_key')
        .eq('api_key', apiKeyData.api_key)
        .single();

      if (existingKey && !checkError) {
        throw new Error('This API key already exists. Please use a different key.');
      }

      // Create a new API key
      const insertData = {
        user_id: user.id,
        company_id: apiKeyData.company_id || null,
        service: apiKeyData.service,
        api_key: apiKeyData.api_key,
        is_used_by: apiKeyData.company_id ? [user.id] : [],
      };

      const { data, error } = await supabase
        .from('api_keys')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error creating API key:', error);
      
      // Provide user-friendly error messages
      if (error.code === '23505' && error.constraint === 'api_keys_unique_value') {
        throw new Error('This API key already exists. Please use a different key.');
      } else if (error.message?.includes('duplicate key value violates unique constraint')) {
        throw new Error('This API key already exists. Please use a different key.');
      } else if (error.message?.includes('You do not have access to this company')) {
        throw new Error('You do not have permission to add API keys for this company.');
      } else if (error.message?.includes('Company not found')) {
        throw new Error('The selected company was not found.');
      } else if (error.message?.includes('User not authenticated')) {
        throw new Error('Please log in to add API keys.');
      }
      
      throw error;
    }
  }

  // Update an API key
  static async updateApiKey(id: string, updates: UpdateApiKeyData): Promise<ApiKey> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Verify user has access to this API key
      const { data: existingKey, error: fetchError } = await supabase
        .from('api_keys')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError || !existingKey) {
        throw new Error('API key not found');
      }

      // Check if user owns the key or has company access
      const hasAccess = existingKey.user_id === user.id || 
        (existingKey.company_id && existingKey.is_used_by?.includes(user.id));

      if (!hasAccess) {
        throw new Error('You do not have permission to update this API key');
      }

      const { data, error } = await supabase
        .from('api_keys')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating API key:', error);
      throw error;
    }
  }

  // Delete an API key
  static async deleteApiKey(id: string): Promise<void> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Verify user has access to this API key
      const { data: existingKey, error: fetchError } = await supabase
        .from('api_keys')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError || !existingKey) {
        throw new Error('API key not found');
      }

      // Check if user owns the key or has company access
      const hasAccess = existingKey.user_id === user.id || 
        (existingKey.company_id && existingKey.is_used_by?.includes(user.id));

      if (!hasAccess) {
        throw new Error('You do not have permission to delete this API key');
      }

      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting API key:', error);
      throw error;
    }
  }

  // Add user to API key usage
  static async addUserToApiKey(apiKeyId: string, userId: string): Promise<ApiKey> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get current API key data
      const { data: apiKey, error: fetchError } = await supabase
        .from('api_keys')
        .select('*')
        .eq('id', apiKeyId)
        .single();

      if (fetchError || !apiKey) {
        throw new Error('API key not found');
      }

      // Check if current user has permission to modify this API key
      const hasAccess = apiKey.user_id === user.id || 
        (apiKey.company_id && apiKey.is_used_by?.includes(user.id));

      if (!hasAccess) {
        throw new Error('You do not have permission to modify this API key');
      }

      // Add user to is_used_by array if not already present
      const currentUsers = apiKey.is_used_by || [];
      if (!currentUsers.includes(userId)) {
        const updatedUsers = [...currentUsers, userId];
        
        const { data, error } = await supabase
          .from('api_keys')
          .update({ is_used_by: updatedUsers })
          .eq('id', apiKeyId)
          .select()
          .single();

        if (error) throw error;
        return data;
      }

      return apiKey;
    } catch (error) {
      console.error('Error adding user to API key:', error);
      throw error;
    }
  }

  // Remove user from API key usage
  static async removeUserFromApiKey(apiKeyId: string, userId: string): Promise<ApiKey> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get current API key data
      const { data: apiKey, error: fetchError } = await supabase
        .from('api_keys')
        .select('*')
        .eq('id', apiKeyId)
        .single();

      if (fetchError || !apiKey) {
        throw new Error('API key not found');
      }

      // Check if current user has permission to modify this API key
      const hasAccess = apiKey.user_id === user.id || 
        (apiKey.company_id && apiKey.is_used_by?.includes(user.id));

      if (!hasAccess) {
        throw new Error('You do not have permission to modify this API key');
      }

      // Remove user from is_used_by array
      const currentUsers = apiKey.is_used_by || [];
      const updatedUsers = currentUsers.filter((id: string) => id !== userId);
      
      const { data, error } = await supabase
        .from('api_keys')
        .update({ is_used_by: updatedUsers })
        .eq('id', apiKeyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error removing user from API key:', error);
      throw error;
    }
  }

  // Get API key by ID
  static async getApiKeyById(id: string): Promise<ApiKey | null> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        throw error;
      }

      // Check if user has access to this API key
      const hasAccess = data.user_id === user.id || 
        (data.company_id && data.is_used_by?.includes(user.id));

      if (!hasAccess) {
        throw new Error('You do not have access to this API key');
      }

      return data;
    } catch (error) {
      console.error('Error fetching API key by ID:', error);
      throw error;
    }
  }

  // Toggle API key active status
  static async toggleApiKeyStatus(id: string): Promise<ApiKey> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get current API key data
      const { data: apiKey, error: fetchError } = await supabase
        .from('api_keys')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError || !apiKey) {
        throw new Error('API key not found');
      }

      // Check if user has permission to modify this API key
      const hasAccess = apiKey.user_id === user.id || 
        (apiKey.company_id && apiKey.is_used_by?.includes(user.id));

      if (!hasAccess) {
        throw new Error('You do not have permission to modify this API key');
      }

      const { data, error } = await supabase
        .from('api_keys')
        .update({ is_active: !apiKey.is_active })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling API key status:', error);
      throw error;
    }
  }

  // Test Mistral API key
  static async testMistralApiKey(apiKey: string, signal?: AbortSignal): Promise<{ success: boolean; poem?: string; error?: string; userMessage?: string; model?: string; usage?: any }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Testing API key for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling edge function...');

      // Create a promise that can be cancelled
      const invokePromise = supabase.functions.invoke('test-mistral', {
        body: { api_key: apiKey }
      });

      let data, error;
      
      // If signal is provided, create a race between the invoke and the abort signal
      if (signal) {
        const abortPromise = new Promise((_, reject) => {
          signal.addEventListener('abort', () => {
            reject(new Error('Operation was cancelled'));
          });
        });

        const result = await Promise.race([invokePromise, abortPromise]) as any;
        data = result.data;
        error = result.error;
      } else {
        const result = await invokePromise;
        data = result.data;
        error = result.error;
      }

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        let userMessage = error.message || 'Failed to test API key';
        
        // Try to extract the detailed error message from the Edge Function's response
        if (error.context && error.context.data) {
          try {
            const errorData = typeof error.context.data === 'string' 
              ? JSON.parse(error.context.data) 
              : error.context.data;
            if (errorData.userMessage) {
              userMessage = errorData.userMessage;
            } else if (errorData.error) {
              userMessage = errorData.error;
            }
          } catch (parseError) {
            console.log('Could not parse error context data:', parseError);
          }
        }
        
        return { 
          success: false, 
          error: error.message || 'Failed to test API key',
          userMessage: userMessage
        };
      }

      // Handle error responses from the function
      if (data && !data.success) {
        return {
          success: false,
          error: data.error || 'API test failed',
          userMessage: data.userMessage || data.error || 'API test failed'
        };
      }

      return data;
    } catch (error) {
      console.error('Error testing Mistral API key:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }


  // Process document with OCR using Mistral AI
  static async processDocumentOCR(documentUrl: string): Promise<{ 
    success: boolean; 
    extractedText?: string; 
    error?: string; 
    userMessage?: string;
    model?: string;
    usage?: any;
    documentSize?: number;
    processingTime?: string;
    apiKeyId?: string;
    keysTried?: number;
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Processing OCR for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling document-ocr edge function...');

      const { data, error } = await supabase.functions.invoke('document-ocr', {
        body: { 
          document_url: documentUrl
        },
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        let userMessage = error.message || 'Failed to process document with OCR';
        
        // Try to extract the detailed error message from the Edge Function's response
        if (error.context && error.context.data) {
          try {
            const errorData = typeof error.context.data === 'string' 
              ? JSON.parse(error.context.data) 
              : error.context.data;
            if (errorData.userMessage) {
              userMessage = errorData.userMessage;
            } else if (errorData.error) {
              userMessage = errorData.error;
            }
          } catch (parseError) {
            console.log('Could not parse error context data:', parseError);
          }
        }
        
        return { 
          success: false, 
          error: error.message || 'Failed to process document with OCR',
          userMessage: userMessage
        };
      }

      // Handle error responses from the function
      if (data && !data.success) {
        return {
          success: false,
          error: data.error || 'OCR processing failed',
          userMessage: data.userMessage || data.error || 'OCR processing failed'
        };
      }

      return data;

    } catch (error) {
      console.error('OCR processing error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while processing the document. Please try again!!!!.'
      };
    }
  }

  // Call mistral-ocr-latest edge function
  static async callMistralOCR(documentUrl?: string, fileName?: string): Promise<{ 
    success: boolean; 
    message?: string;
    timestamp?: string;
    error?: string; 
    userMessage?: string;
    bucketAccess?: boolean;
    bucketMessage?: string;
    documentFound?: boolean;
    documentMessage?: string;
    documentSize?: number;
    documentSizeFormatted?: string;
    ocrResult?: {
      success: boolean;
      extractedText?: string;
      formattingInfo?: {
        headings: Array<{level: number, text: string, page: number}>;
        boldText: Array<{text: string, page: number}>;
        italicText: Array<{text: string, page: number}>;
        lists: Array<{type: 'ordered' | 'unordered', items: string[], page: number}>;
        tables: Array<{rows: string[][], page: number}>;
        links: Array<{text: string, url: string, page: number}>;
      };
      error?: string;
      userMessage?: string;
      model?: string;
      usage?: any;
      apiKeyId?: string;
      processingTime?: string;
      details?: string;
    };
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Calling mistral-ocr-latest for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling mistral-ocr-latest edge function...');

      const { data, error } = await supabase.functions.invoke('mistral-ocr-latest', {
        body: {
          documentUrl: documentUrl,
          fileName: fileName
        }
      });

      console.log('Mistral OCR response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to call mistral OCR',
          userMessage: 'Failed to connect to mistral OCR function. Please try again.'
        };
      }

      // Handle error responses from the function
      if (data && !data.success) {
        return {
          success: false,
          error: data.error || 'Mistral OCR call failed',
          userMessage: data.error || 'Mistral OCR call failed'
        };
      }

      return data;

    } catch (error) {
      console.error('Mistral OCR error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while calling mistral OCR. Please try again.'
      };
    }
  }

  // Test connection to edge functions
  static async testConnection(signal?: AbortSignal): Promise<{ 
    success: boolean; 
    message?: string;
    timestamp?: string;
    error?: string; 
    userMessage?: string;
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Testing connection for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling test-connection edge function...');

      // Create a promise that can be cancelled
      const invokePromise = supabase.functions.invoke('test-connection', {
        body: {}
      });

      let data, error;
      
      // If signal is provided, create a race between the invoke and the abort signal
      if (signal) {
        const abortPromise = new Promise((_, reject) => {
          signal.addEventListener('abort', () => {
            reject(new Error('Operation was cancelled'));
          });
        });

        const result = await Promise.race([invokePromise, abortPromise]) as any;
        data = result.data;
        error = result.error;
      } else {
        const result = await invokePromise;
        data = result.data;
        error = result.error;
      }

      console.log('Test connection response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to test connection',
          userMessage: 'Failed to connect to edge function. Please try again.'
        };
      }

      // Handle error responses from the function
      if (data && !data.success) {
        return {
          success: false,
          error: data.error || 'Connection test failed',
          userMessage: data.error || 'Connection test failed'
        };
      }

      return data;

    } catch (error) {
      console.error('Test connection error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while testing connection. Please try again.'
      };
    }
  }

  // Summarize text using mistral-text-summarize edge function
  static async summarizeText(ocrData: string, summaryType: 'brief' | 'detailed' | 'key-points' = 'detailed', maxLength: number = 500, documentId?: string, companyId?: string): Promise<{ 
    success: boolean; 
    summary?: string;
    keyPoints?: string[];
    wordCount?: number;
    originalLength?: number;
    compressionRatio?: number;
    model?: string;
    usage?: any;
    error?: string; 
    userMessage?: string;
    timestamp?: string;
    context?: {
      accessType: 'user' | 'company';
      userId?: string;
      companyId?: string;
      companyName?: string;
      hasApiKeys?: boolean;
      apiKeyCount?: number;
    };
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Summarizing text for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling mistral-text-summarize edge function...');

      const { data, error } = await supabase.functions.invoke('mistral-text-summarize', {
        body: {
          documentId,
          ocrData,
          summaryType,
          maxLength,
          companyId
        }
      });

      console.log('Text summarization response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to summarize text',
          userMessage: 'Failed to connect to summarization service. Please try again.'
        };
      }

      // Handle error responses from the function
      if (data && !data.success) {
        return {
          success: false,
          error: data.error || 'Text summarization failed',
          userMessage: data.userMessage || data.error || 'Text summarization failed'
        };
      }

      return data;

    } catch (error) {
      console.error('Text summarization error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while summarizing the text. Please try again.'
      };
    }
  }

  // Analyze skills using mistral-skills edge function
  static async analyzeSkills(ocrData: string, summaryType: 'brief' | 'detailed' | 'key-points' = 'detailed', maxLength: number = 500, documentId?: string, companyId?: string): Promise<{ 
    success: boolean; 
    skills?: string[];
    skillCount?: number;
    originalLength?: number;
    model?: string;
    usage?: any;
    error?: string; 
    userMessage?: string;
    timestamp?: string;
    context?: {
      accessType: 'user' | 'company';
      userId?: string;
      companyId?: string;
      companyName?: string;
      hasApiKeys?: boolean;
      apiKeyCount?: number;
    };
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Analyzing skills for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling mistral-skills edge function...');

      const { data, error } = await supabase.functions.invoke('mistral-skills', {
        body: {
          documentId,
          ocrData,
          companyId
        }
      });

      console.log('Skill analysis response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to analyze skills',
          userMessage: 'Failed to connect to skill analysis service. Please try again.'
        };
      }

      // Handle error responses from the function
      if (data && !data.success) {
        return {
          success: false,
          error: data.error || 'Skill analysis failed',
          userMessage: data.userMessage || data.error || 'Skill analysis failed'
        };
      }

      return data;

    } catch (error) {
      console.error('Skill analysis error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while analyzing skills. Please try again.'
      };
    }
  }

  // Check if document exists in storage (lightweight check before OCR)
  static async checkDocumentExists(documentUrl: string): Promise<{ 
    success: boolean; 
    documentExists: boolean;
    bucket?: string;
    filePath?: string;
    message?: string;
    error?: string; 
    userMessage?: string;
    details?: string;
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Checking document existence for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling check-document edge function...');

      const { data, error } = await supabase.functions.invoke('check-document', {
        body: { 
          document_url: documentUrl
        },
      });

      console.log('Edge function response:', { data, error });
      console.log('Edge function data details:', {
        success: data?.success,
        documentExists: data?.documentExists,
        bucket: data?.bucket,
        filePath: data?.filePath,
        message: data?.message,
        error: data?.error,
        userMessage: data?.userMessage
      });

      if (error) {
        console.error('Edge function error:', error);
        let userMessage = error.message || 'Failed to check document existence';
        
        // Try to extract the detailed error message from the Edge Function's response
        if (error.context && error.context.data) {
          try {
            const errorData = typeof error.context.data === 'string' 
              ? JSON.parse(error.context.data) 
              : error.context.data;
            if (errorData.userMessage) {
              userMessage = errorData.userMessage;
            } else if (errorData.error) {
              userMessage = errorData.error;
            }
          } catch (parseError) {
            console.log('Could not parse error context data:', parseError);
          }
        }
        
        return { 
          success: false, 
          documentExists: false,
          error: error.message || 'Failed to check document existence',
          userMessage: userMessage
        };
      }

      // Handle error responses from the function
      if (data && !data.success) {
        return {
          success: false,
          documentExists: false,
          error: data.error || 'Document check failed',
          userMessage: data.userMessage || data.error || 'Document check failed',
          details: data.details
        };
      }

      return data;

    } catch (error) {
      console.error('Document existence check error:', error);
      return {
        success: false,
        documentExists: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while checking the document. Please try again.'
      };
    }
  }

  // Get service options
  static getServiceOptions(): Array<{ value: string; label: string; description: string }> {
    return [
      { value: 'groq', label: 'Groq', description: 'Fast AI inference platform' },
      { value: 'mistral', label: 'Mistral AI', description: 'Open-source AI models' },
      { value: 'openai', label: 'OpenAI', description: 'GPT models and API' },
      { value: 'anthropic', label: 'Anthropic', description: 'Claude AI models' },
      { value: 'google', label: 'Google AI', description: 'Gemini and other Google AI services' },
      { value: 'azure', label: 'Azure OpenAI', description: 'Microsoft Azure AI services' },
      { value: 'other', label: 'Other', description: 'Other AI service providers' },
    ];
  }

  // Summarize document using Mistral AI
  static async summarizeDocument(documentId: string, ocrText?: string, summaryType: 'brief' | 'detailed' | 'key_points' = 'detailed', maxLength: number = 500): Promise<{
    success: boolean;
    summary?: string;
    key_points?: string[];
    word_count?: number;
    original_length?: number;
    compression_ratio?: number;
    model?: string;
    usage?: any;
    error?: string;
    userMessage?: string;
    keysTried?: number;
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Summarizing document for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling mistral-summarize edge function...');

      const { data, error } = await supabase.functions.invoke('mistral-summarize', {
        body: { 
          document_id: documentId,
          ocr_text: ocrText,
          summary_type: summaryType,
          max_length: maxLength
        },
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        return {
          success: false,
          error: error.message,
          userMessage: `Failed to generate summary: ${error.message}. Please check the edge function logs for more details.`
        };
      }

      if (!data) {
        return {
          success: false,
          error: 'No response from summarization service',
          userMessage: 'No response received from the summarization service. The edge function may not be deployed or may have crashed.'
        };
      }

      console.log('Edge function response data:', data);
      return data;

    } catch (error) {
      console.error('Error in summarizeDocument:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while generating the summary.'
      };
    }
  }

  // Test function for the new edge function
  static async testNewEdgeFunction(): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    userMessage?: string;
    debugInfo?: any;
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Testing new edge function for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling mistral-summarize-new edge function...');

      const { data, error } = await supabase.functions.invoke('mistral-summarize-new', {
        body: { 
          test: true,
          timestamp: new Date().toISOString()
        },
      });

      console.log('New edge function response:', { data, error });

      if (error) {
        console.error('New edge function error:', error);
        return {
          success: false,
          error: error.message,
          userMessage: `Failed to call new edge function: ${error.message}. Please check the edge function logs for more details.`
        };
      }

      if (!data) {
        return {
          success: false,
          error: 'No response from new edge function',
          userMessage: 'No response received from the new edge function. The edge function may not be deployed or may have crashed.'
        };
      }

      console.log('New edge function response data:', data);
      return data;

    } catch (error) {
      console.error('Error in testNewEdgeFunction:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while testing the new edge function.'
      };
    }
  }

  // Summarize text using mistral-text-summarize-user edge function (for user context)
  static async summarizeTextUser(ocrData: string, summaryType: 'brief' | 'detailed' | 'key-points' = 'detailed', maxLength: number = 500, documentId?: string): Promise<{ 
    success: boolean; 
    summary?: string;
    keyPoints?: string[];
    wordCount?: number;
    originalLength?: number;
    compressionRatio?: number;
    model?: string;
    usage?: any;
    error?: string; 
    userMessage?: string;
    timestamp?: string;
    context?: {
      accessType: 'user' | 'company';
      userId?: string;
      companyId?: string;
      companyName?: string;
      hasApiKeys?: boolean;
      apiKeyCount?: number;
    };
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Summarizing text for user (user-specific function):', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling mistral-text-summarize-user edge function...');

      const { data, error } = await supabase.functions.invoke('mistral-text-summarize-user', {
        body: {
          documentId,
          ocrData,
          summaryType,
          maxLength
        }
      });

      console.log('User text summarization response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        return {
          success: false,
          error: error.message || 'Edge function call failed',
          userMessage: 'Failed to connect to summarization service. Please try again.'
        };
      }

      if (!data) {
        return {
          success: false,
          error: 'No response from edge function',
          userMessage: 'No response received from summarization service. Please try again.'
        };
      }

      return data;

    } catch (error) {
      console.error('Error in summarizeTextUser:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while generating the summary.'
      };
    }
  }

  // Call mistral-image-analysis edge function
  static async callImageAnalysis(documentUrl?: string, fileName?: string): Promise<{ 
    success: boolean; 
    message?: string;
    timestamp?: string;
    error?: string; 
    userMessage?: string;
    bucketAccess?: boolean;
    bucketMessage?: string;
    documentFound?: boolean;
    documentMessage?: string;
    documentSize?: number;
    documentSizeFormatted?: string;
    analysisResult?: {
      description?: string;
      objects?: string[];
      text?: string;
      faces?: number;
      colors?: string[];
      scene?: string;
      confidence?: number;
      model?: string;
      apiKeyId?: string;
    };
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Calling mistral-image-analysis for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling mistral-image-analysis edge function...');

      const { data, error } = await supabase.functions.invoke('mistral-image-analysis', {
        body: {
          documentUrl: documentUrl,
          fileName: fileName
        }
      });

      console.log('Mistral Image Analysis response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to call mistral image analysis',
          userMessage: 'Failed to connect to mistral image analysis function. Please try again.'
        };
      }

      // Handle error responses from the function
      if (data && !data.success) {
        return {
          success: false,
          error: data.error || 'Mistral image analysis call failed',
          userMessage: data.error || 'Mistral image analysis call failed'
        };
      }

      return data;

    } catch (error) {
      console.error('Mistral Image Analysis error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while calling mistral image analysis. Please try again.'
      };
    }
  }

  // Call mistral-image-tags edge function
  static async callImageTags(documentUrl?: string, fileName?: string): Promise<{ 
    success: boolean; 
    message?: string;
    timestamp?: string;
    error?: string; 
    userMessage?: string;
    bucketAccess?: boolean;
    bucketMessage?: string;
    documentFound?: boolean;
    documentMessage?: string;
    documentSize?: number;
    documentSizeFormatted?: string;
    tagsResult?: {
      tags?: string[];
      categories?: {
        objects: string[];
        people: string[];
        colors: string[];
        locations: string[];
        activities: string[];
        emotions: string[];
        other: string[];
      };
      confidence?: number;
      model?: string;
      apiKeyId?: string;
    };
  }> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('User not authenticated');
      }

      console.log('Calling mistral-image-tags for user:', user.id);

      // Get the current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        console.error('Session error:', sessionError);
        throw new Error('No valid session found');
      }

      console.log('Session found, calling mistral-image-tags edge function...');

      const { data, error } = await supabase.functions.invoke('mistral-image-tags', {
        body: {
          documentUrl: documentUrl,
          fileName: fileName
        }
      });

      console.log('Mistral Image Tags response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to call mistral image tags',
          userMessage: 'Failed to connect to mistral image tags function. Please try again.'
        };
      }

      // Handle error responses from the function
      if (data && !data.success) {
        return {
          success: false,
          error: data.error || 'Mistral image tags call failed',
          userMessage: data.error || 'Mistral image tags call failed'
        };
      }

      return data;

    } catch (error) {
      console.error('Mistral Image Tags error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        userMessage: 'An unexpected error occurred while calling mistral image tags. Please try again.'
      };
    }
  }
}
